import { useCallback, useState } from "react";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { queryClient } from "App";
import ACL_RELATIONSHIPS from "acl-relationships";
import { Button } from "@/components/ui/button";
import Authorized from "components/Authorized";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import { formatError } from "helpers/formatError";
import { useCustomEmailDomainsTable } from "hooks/useCustomEmailDomainsTable";
import CustomEmailDomainsService from "services/CustomEmailDomains";
import DomainModal from "./components/DomainModal";

export const CUSTOM_EMAIL_DOMAINS_TABLE_CONSTANTS = {
  tableName: "custom_email_domains",
  queryKey: "custom_email_domains",
  className: "MailgunDomain",
  header: "Custom Email Domains",
};

// TableTop renders a string headerIcon through Semantic's Icon; passing a node
// keeps this page free of Semantic UI.
const headerIcon = (
  <span
    className="tw:inline-flex tw:size-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-primary tw:text-primary-foreground"
    aria-hidden="true"
  >
    <Mail className="tw:size-4" />
  </span>
);

export const invalidateCustomEmailDomainsTableQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [CUSTOM_EMAIL_DOMAINS_TABLE_CONSTANTS.queryKey],
  });
};

const CustomEmailDomainsTableV3 = () => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const handleSetAsDefault = useCallback(async (domainId, isDefault) => {
    // Optimistic update across every page and filter combination of this
    // table, which the key prefix matches regardless of queryParams.
    queryClient.setQueriesData(
      { queryKey: [CUSTOM_EMAIL_DOMAINS_TABLE_CONSTANTS.queryKey] },
      oldData => {
        if (!oldData?.data) return oldData;
        return {
          ...oldData,
          data: oldData.data.map(row => ({
            ...row,
            is_default: row.id === domainId && isDefault,
          })),
        };
      }
    );

    try {
      await CustomEmailDomainsService.updateDomain(domainId, { isDefault });
      toast.success("Custom email domain updated");
    } catch (err) {
      toast.error(formatError(err, "Failed to update custom email domain"));
    } finally {
      // Refetching also rolls back the optimistic update when the request fails.
      await invalidateCustomEmailDomainsTableQuery();
    }
  }, []);

  const { columns } = useCustomEmailDomainsTable({
    tableName: CUSTOM_EMAIL_DOMAINS_TABLE_CONSTANTS.tableName,
    handleSetAsDefault,
  });

  const fetchDomains = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);
        const response =
          await CustomEmailDomainsService.getDomains(nextQueryParams);

        return response;
      } catch (error) {
        console.error("Error fetching custom email domains:", error);
        throw error;
      }
    },
    [columns]
  );

  return (
    <>
      <DomainModal open={isCreateOpen} onOpenChange={setIsCreateOpen} />
      <BaseTableV3
        columns={columns}
        fetchData={fetchDomains}
        queryKey={CUSTOM_EMAIL_DOMAINS_TABLE_CONSTANTS.queryKey}
        className={CUSTOM_EMAIL_DOMAINS_TABLE_CONSTANTS.className}
        tableName={CUSTOM_EMAIL_DOMAINS_TABLE_CONSTANTS.tableName}
        header={CUSTOM_EMAIL_DOMAINS_TABLE_CONSTANTS.header}
        headerIcon={headerIcon}
        createButton={
          <Authorized
            permissions={[ACL_RELATIONSHIPS.customEmailDomains.create]}
          >
            <Button size="sm" onClick={() => setIsCreateOpen(true)}>
              New Domain
            </Button>
          </Authorized>
        }
        enableSearch
        enableSettings
        invalidateData={invalidateCustomEmailDomainsTableQuery}
      />
    </>
  );
};

export default CustomEmailDomainsTableV3;
